import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import SealedVault from "../components/SealedVault";
import ComparisonTable from "../components/ComparisonTable";
import ComplianceBadge from "../components/ComplianceBadge";
import VolatilityWidget from "../components/VolatilityWidget";
import RiskAnalysisPanel from "../components/RiskAnalysisPanel";
import AwardsPanel from "../components/AwardsPanel";
import BidChatPanel from "../components/BidChatPanel";
import EmailDraftModal from "../components/EmailDraftModal";
import { ArrowLeft, Copy, Calendar, Building2, Loader2, Mail, Paperclip } from "lucide-react";
import { toast } from "sonner";
import FileUploader from "../components/FileUploader";
import FileList from "../components/FileList";

const CATEGORIES = ["Piping", "Instrumentation", "Heavy Haul", "HSE", "Labor"];

export default function BidDetail() {
  const { rfpId } = useParams();
  const navigate = useNavigate();
  const [rfp, setRfp] = useState(null);
  const [activeCat, setActiveCat] = useState(CATEGORIES[0]);
  const [emailModalFor, setEmailModalFor] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/rfps/${rfpId}`);
        setRfp(data);
      } catch { toast.error("Bid not found"); navigate("/dashboard"); }
    };
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [rfpId, navigate]);

  const copyInvite = (srfqId) => {
    const url = `${window.location.origin}/vendor/quote/${srfqId}`;
    navigator.clipboard.writeText(url);
    toast.success("Vendor invite link copied");
  };

  if (!rfp) {
    return <Layout><div className="text-center py-20 font-mono-data text-xs text-text-secondary animate-pulse">Opening folder...</div></Layout>;
  }

  const activeSub = rfp.sub_rfqs_data?.find(s => s.category === activeCat);

  return (
    <Layout sidebar={<VolatilityWidget />}>
      <button onClick={() => navigate("/dashboard")} data-testid="back-to-desk-btn"
        className="font-mono-data text-xs uppercase tracking-widest text-text-secondary hover:text-white inline-flex items-center gap-2 mb-6">
        <ArrowLeft className="w-3 h-3" /> Back to Desk
      </button>

      <div className="tray p-7 mb-6" data-testid="bid-detail-header">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="font-mono-data text-[10px] tracking-[0.3em] uppercase text-amber-500 mb-2">
              {rfp.id.toUpperCase()} · {rfp.status.toUpperCase()}
            </div>
            <h1 className="font-display text-4xl mb-2">{rfp.title}</h1>
            <div className="flex items-center gap-5 text-sm text-text-secondary">
              <span className="inline-flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> {rfp.operator}</span>
              <span className="inline-flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> {rfp.deadline}</span>
              {rfp.priority === "high" && <span className="stamp stamp-warn">Priority</span>}
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary">Line Items</div>
            <div className="font-display text-4xl text-amber-500">{rfp.line_items?.length || 0}</div>
          </div>
        </div>

        {rfp.status === "extracting" && (
          <div className="mt-3 inline-flex items-center gap-2 font-mono-data text-xs text-amber-500">
            <Loader2 className="w-3 h-3 animate-spin" /> AI agent parsing document...
          </div>
        )}
      </div>

      {/* AI Insights row */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <RiskAnalysisPanel rfpId={rfpId} />
        <AwardsPanel rfpId={rfpId} />
      </div>

      {/* Reference Documents */}
      <div className="tray p-6 mb-6" data-testid="rfp-reference-docs">
        <div className="flex items-center gap-3 mb-4">
          <Paperclip className="w-5 h-5 text-amber-500" />
          <div>
            <div className="font-mono-data text-[10px] tracking-widest uppercase text-amber-500">Reference Documents</div>
            <div className="font-display text-xl">Drawings · Addenda · Site Photos</div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <FileUploader scope="rfp_reference" scopeId={rfpId} label="Attach reference doc" />
          <FileList scope="rfp_reference" scopeId={rfpId} emptyLabel="No reference documents attached" />
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto" data-testid="category-tabs">
        {CATEGORIES.map(cat => {
          const sub = rfp.sub_rfqs_data?.find(s => s.category === cat);
          const count = sub?.line_items?.length ?? 0;
          const quotes = sub?.quotes?.length ?? 0;
          return (
            <button
              key={cat}
              onClick={() => setActiveCat(cat)}
              data-testid={`tab-${cat.toLowerCase().replace(' ', '-')}`}
              className={`tray-inset px-4 py-3 transition whitespace-nowrap ${activeCat === cat ? "ring-2 ring-amber-500" : ""}`}
            >
              <div className="font-mono-data text-[9px] tracking-[0.2em] uppercase text-text-secondary">{cat}</div>
              <div className="flex items-baseline gap-2 mt-0.5">
                <span className="font-display text-lg">{count}</span>
                <span className="font-mono-data text-[10px] text-text-secondary">items · {quotes} quotes</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Active sub-RFQ panel */}
      {activeSub ? (
        <div className="space-y-6">
          {/* Sub-RFQ header */}
          <div className="tray p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="font-mono-data text-[10px] tracking-widest uppercase text-amber-500">Sub-RFQ · {activeSub.category}</div>
                <h2 className="font-display text-2xl mt-1">{activeSub.line_items.length} line items</h2>
              </div>
              <button onClick={() => copyInvite(activeSub.id)} data-testid="copy-invite-btn"
                className="btn-tactile inline-flex items-center gap-2 text-xs">
                <Copy className="w-3 h-3" /> Copy Vendor Invite
              </button>
              <button onClick={() => setEmailModalFor(activeSub)} data-testid="email-draft-btn"
                className="btn-tactile btn-amber inline-flex items-center gap-2 text-xs ms-2">
                <Mail className="w-3 h-3" /> AI Email Draft
              </button>
            </div>

            <div className="tray-inset p-4 overflow-x-auto">
              <table className="w-full font-mono-data text-xs">
                <thead>
                  <tr className="text-text-secondary uppercase tracking-widest text-[10px]">
                    <th className="text-left py-2">Description</th>
                    <th className="text-right py-2 w-24">Qty</th>
                    <th className="text-left py-2 w-20">Unit</th>
                    <th className="text-left py-2 w-48">Spec</th>
                  </tr>
                </thead>
                <tbody>
                  {activeSub.line_items.map((li) => (
                    <tr key={li.id} className="border-t border-white/5">
                      <td className="py-2 text-white">{li.description}</td>
                      <td className="text-right py-2">{li.quantity}</td>
                      <td className="py-2 text-text-secondary">{li.unit}</td>
                      <td className="py-2 text-amber-500/80">{li.spec}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Quotes */}
          <div>
            <h3 className="font-display text-2xl mb-4">Vendor Quotes · {activeSub.quotes?.length || 0}</h3>
            {(activeSub.quotes?.length || 0) === 0 ? (
              <div className="tray-inset p-8 text-center font-mono-data text-xs text-text-secondary">
                No vendor quotes yet. Share the invite link with vendors.
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {activeSub.quotes.map(q => (
                  <div key={q.id} className="tray p-5" data-testid={`quote-card-${q.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="font-display text-xl">{q.company}</div>
                        <div className="font-mono-data text-[10px] text-text-secondary uppercase tracking-widest">
                          {q.vendor_name}
                        </div>
                      </div>
                      <ComplianceBadge check={q.compliance_check} />
                    </div>
                    <SealedVault
                      quote={q}
                      onUpdate={(updated) => {
                        const newRfp = { ...rfp };
                        const sub = newRfp.sub_rfqs_data.find(s => s.id === activeSub.id);
                        const idx = sub.quotes.findIndex(x => x.id === updated.id);
                        if (idx >= 0) sub.quotes[idx] = updated;
                        setRfp(newRfp);
                      }}
                    />
                    {q.hse_approved && (
                      <div className="mt-3 tray-inset p-3">
                        <div className="font-mono-data text-[10px] tracking-widest uppercase text-text-secondary mb-2 inline-flex items-center gap-1">
                          <Paperclip className="w-3 h-3" /> Vendor Attachments
                        </div>
                        <FileList
                          scope="vendor_quote_attachment"
                          scopeId={q.id}
                          emptyLabel="No documents shared by vendor"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <ComparisonTable quotes={activeSub.quotes || []} />

            {/* Awarded Contract slot */}
            <div className="tray p-5 mt-6" data-testid="awarded-contract-slot">
              <div className="flex items-center gap-3 mb-3">
                <Paperclip className="w-4 h-4 text-amber-500" />
                <div>
                  <div className="font-mono-data text-[10px] tracking-widest uppercase text-amber-500">Award Vault</div>
                  <div className="font-display text-lg">Signed Contract · {activeSub.category}</div>
                  <div className="text-xs text-text-secondary mt-0.5">Store the executed contract PDF once a winner is chosen</div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <FileUploader scope="awarded_contract" scopeId={activeSub.id} label="Attach signed contract" />
                <FileList scope="awarded_contract" scopeId={activeSub.id} emptyLabel="No contract yet" />
              </div>
            </div>
          </div>
        </div>
      ) : rfp.status === "extracting" ? (
        <div className="tray p-10 text-center">
          <Loader2 className="w-10 h-10 text-amber-500 animate-spin mx-auto mb-4" />
          <div className="font-display text-xl">Generating Sub-RFQs</div>
          <p className="text-text-secondary mt-2">AI agent is parsing line items and assigning categories...</p>
        </div>
      ) : (
        <div className="tray p-10 text-center font-mono-data text-xs text-text-secondary">
          No Sub-RFQ for {activeCat} in this RFP.
        </div>
      )}

      {/* AI Bid Assistant - floating */}
      <BidChatPanel rfpId={rfpId} rfpTitle={rfp.title} />

      {/* AI Email Draft modal */}
      <EmailDraftModal
        subRfqId={emailModalFor?.id}
        subRfqLabel={emailModalFor ? `${emailModalFor.category} · ${rfp.title}` : ""}
        open={!!emailModalFor}
        onClose={() => setEmailModalFor(null)}
      />
    </Layout>
  );
}
